import { LoadingOutlined } from '@ant-design/icons';
import { useMount } from 'ahooks';
import { Modal, message } from 'antd';
import useModal from 'hooks/useModal';
import { ProviderProps, useCallback, useContext, useMemo, useRef } from 'react';
import { useTranslate } from '@tolgee/react';
import createNamedContext from 'utils/createNamedContext';
import { taskList } from 'views/Todo/service';
import { TodoTaskPolicy } from './TaskPolicy';
import {
  Container,
  modalClassName,
} from 'views/Conversation/Chat/components/MessageScenario/ScenarioModal';
import PermissionTips from 'views/Conversation/Chat/components/MessageScenario/PermissionTips';
import { permissionNameIdMap, useGetPermission } from 'hooks/business/useNewPermisson';
import { ReactScrollProvider } from 'views/Aide/components/ReactScrollContext';
import { useQuery } from '@tanstack/react-query';
import { ApiQueryKey } from 'services/config/queryKey';
import { scheduleIdle } from 'utils/scheduleIdle';

interface ShowPolicyProps {
  platform?: General.PlatformUpperCase;
  region?: General.UpperCaseRegion;
  storeId: number;
  type?: Todo.Type;
  resultType?: Todo.ResultType;
  taskId?: number;
  activeKey?: '1' | '2';
  closeCallback?: () => void;
}

export interface TodoContextValue {
  showPolicy: (props: ShowPolicyProps) => void;
}

export const TodoContext = createNamedContext<TodoContextValue | null>('TodoContext', null);

export function useTodoContext() {
  const value = useContext(TodoContext);

  if (value === null) {
    throw new Error(
      'useTodoContext hook should be used in a context of TodoContextProvider component',
    );
  }

  return value;
}

export function TodoContextProvider({
  children,
  ...otherProps
}: Omit<ProviderProps<TodoContextValue>, 'value'>) {
  const { t } = useTranslate();

  const { data: list, refetch } = useQuery({
    queryKey: [ApiQueryKey.ToDoTaskList],
    queryFn: () => taskList(true),
    enabled: false,
    staleTime: 3 * 60 * 1000,
  });

  useMount(() => {
    scheduleIdle(() => {
      refetch();
    });
  });

  const { visible, show, hide, meta } = useModal<ShowPolicyProps & { taskId: number }>();
  const closeCallbackRef = useRef<() => void>();

  const showPolicy = useCallback<TodoContextValue['showPolicy']>(
    (props) => {
      const { platform, region, storeId, taskId, closeCallback } = props;
      closeCallback && (closeCallbackRef.current = closeCallback);

      const task = list?.find(({ storeList }) =>
        storeList.some((platformConfig) => {
          if (platformConfig.platform !== platform) {
            return false;
          }

          return platformConfig.regionStoreScopes.some((regionConfig) => {
            if (regionConfig.region === 'all') {
              return true;
            }

            if (regionConfig.region !== region) {
              return false;
            }

            if ((regionConfig.storeIds || []).length === 0 && regionConfig.storeCount > 0) {
              return true;
            }

            return regionConfig.storeIds?.includes(storeId);
          });
        }),
      );

      if (taskId) {
        show({
          taskId,
          ...props,
        });
      } else if (task) {
        show({
          taskId: task.id,
          ...props,
        });
      } else {
        message.warn(t('todo.待办任务中未找到该店铺'));
      }
    },
    [list, show, t],
  );

  const getPermission = useGetPermission();

  const permission = useMemo(() => {
    return meta
      ? getPermission('访问待办管理中心', {
          platform: meta.platform,
          region: meta.region,
          storeId: meta.storeId,
        })
      : false;
  }, [getPermission, meta]);

  const tips = meta && (
    <PermissionTips
      permission={permission}
      permissionId={permissionNameIdMap.访问待办管理中心}
      storeId={meta.storeId}
    />
  );

  const modal = visible && (
    <Modal
      title={t('todo.待办')}
      width="80vw"
      visible={visible}
      onCancel={() => {
        hide();
        closeCallbackRef.current?.();
      }}
      footer={false}
      className={modalClassName}
    >
      <ReactScrollProvider>
        <Container>
          {tips}
          {meta ? (
            <TodoTaskPolicy
              routeMode={false}
              value={meta}
              readonly={!permission}
              onChange={() => {}}
              onPolicyBack={() => hide()}
            />
          ) : (
            <LoadingOutlined />
          )}
        </Container>
      </ReactScrollProvider>
    </Modal>
  );

  const value = useMemo(() => {
    return { showPolicy };
  }, [showPolicy]);

  return (
    <TodoContext.Provider value={value} {...otherProps}>
      {children}
      {modal}
    </TodoContext.Provider>
  );
}
